import type { ReactNode } from 'react';
import { darkTheme } from '../../../lib/tokens';
import { GATE_MODE_LABEL, type GateMode, type PipelineGate } from '../data/types';

interface GateTableProps {
  gates: PipelineGate[];
  large?: boolean;
}

/**
 * Die Waechter-Tabelle. Der Modus bekommt eine eigene Spalte mit eigener
 * Farbe, weil der Unterschied zwischen "gegen Null" und "Ratchet" der
 * eigentliche Inhalt ist: ein Gate, das den vollen Rueckstand sofort meldet,
 * steht ab Tag 1 rot und wird abgeschaltet.
 *
 * Die Spalte "blind fuer" ist Absicht. Ein Waechter ohne benannte Blindstelle
 * wird fuer allwissend gehalten — und das ist die gefaehrlichere Luege.
 */
export function GateTable({ gates, large = false }: GateTableProps): JSX.Element {
  return (
    <div className="-mx-1 overflow-x-auto px-1">
      <table className={`w-full min-w-[46rem] border-collapse ${large ? 'text-base' : 'text-sm'}`}>
        <thead>
          <tr className="text-left text-text-tertiary">
            <Th large={large}>Wächter</Th>
            <Th large={large}>Zusage</Th>
            <Th large={large}>Modus</Th>
            <Th large={large}>Stand</Th>
            <Th large={large}>Blind für</Th>
          </tr>
        </thead>
        <tbody>
          {gates.map((gate) => (
            <tr key={gate.name} className="border-t align-top" style={{ borderColor: darkTheme.colors.borderSubtle }}>
              <td className="py-3 pr-4">
                <span className="font-semibold text-text-primary">{gate.name}</span>
                {gate.command && (
                  <code className="mt-1 block font-mono text-[11px] text-text-tertiary">{gate.command}</code>
                )}
              </td>
              <td className="py-3 pr-4 text-text-secondary">{gate.promise}</td>
              <td className="py-3 pr-4">
                <ModeBadge mode={gate.mode} />
              </td>
              <td className="py-3 pr-4 text-text-secondary">{gate.current ?? '—'}</td>
              <td className="py-3 text-text-tertiary">{gate.blindFor ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const MODE_COLOR: Record<GateMode, { color: string; bg: string }> = {
  'gegen-null': { color: darkTheme.colors.success, bg: darkTheme.colors.successBg },
  ratchet: { color: darkTheme.colors.info, bg: darkTheme.colors.infoBg },
  manuell: { color: darkTheme.colors.warning, bg: darkTheme.colors.warningBg },
  test: { color: darkTheme.colors.textSecondary, bg: darkTheme.colors.bgElevated },
};

function ModeBadge({ mode }: { mode: GateMode }): JSX.Element {
  const c = MODE_COLOR[mode];
  return (
    <span
      className="inline-block whitespace-nowrap rounded-sm px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider"
      style={{ background: c.bg, color: c.color }}
    >
      {GATE_MODE_LABEL[mode]}
    </span>
  );
}

function Th({ children, large }: { children: ReactNode; large: boolean }): JSX.Element {
  return (
    <th className={`pb-2 pr-4 font-semibold uppercase tracking-wider ${large ? 'text-xs' : 'text-[10px]'}`}>
      {children}
    </th>
  );
}
